// Europe Paris time
// DeviceManager.jsx
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import logo from '../images/247Logo.png';
import { apiFetch } from '../api/apiClient';
import '../styles/DeviceManager.css';
import { DateTime} from 'luxon';
import ethernetIcon from '../icons/ethernet.svg';
import cellularIcon from '../icons/cellular.svg';
import modemIcon from '../icons/modem.svg';
import vlanIcon from '../icons/vlan.svg';
import globeIcon from '../icons/globe.svg';
import wifiIcon from '../icons/wifi.svg';
import FleetHeader from './FleetHeader';
import { usePageTour } from './tour/tourStore';
import '../styles/fleet-theme.css';

function DeviceManager({ user, onLogout }) {
    const [devices, setDevices] = useState({ configured: [], orphaned: [] });
    const [loading, setLoading] = useState(true);
    const [newDevice, setNewDevice] = useState({ serial: '', orgId: '' });
    const [lastUpdated, setLastUpdated] = useState(null);
    const [deviceAlertStatus, setDeviceAlertStatus] = useState({});
    const [message, setMessage] = useState({ text: '', type: '' });
    const loadingVideo = process.env.PUBLIC_URL + '/Logo_video.mp4';
    
    // New state for dropdowns
    const [organizations, setOrganizations] = useState([]);
    const [selectedOrg, setSelectedOrg] = useState('');
    const [orgDevices, setOrgDevices] = useState([]);
    const [selectedDevice, setSelectedDevice] = useState('');
    const [deviceSearchQuery, setDeviceSearchQuery] = useState('');
    const [showDeviceDropdown, setShowDeviceDropdown] = useState(false);
    const [loadingOrgs, setLoadingOrgs] = useState(false);
    const [loadingDevices, setLoadingDevices] = useState(false);
    
    // Bulk add modal state
    const [showBulkModal, setShowBulkModal] = useState(false);
    const [bulkDevices, setBulkDevices] = useState([]);
    const [selectedBulkDevices, setSelectedBulkDevices] = useState(new Set());
    const [onlyOnline, setOnlyOnline] = useState(false);
    const [bulkLoading, setBulkLoading] = useState(false);
    const [showEmailConfirm, setShowEmailConfirm] = useState(false);
    
    // VLAN Modal State
    const [showVlanModal, setShowVlanModal] = useState(false);
    const [selectedDeviceForVlan, setSelectedDeviceForVlan] = useState(null);
    const [vlanCaps, setVlanCaps] = useState([]);
    const [vlanLoading, setVlanLoading] = useState(false);
    const [vlanSaving, setVlanSaving] = useState(false);

    const [originalVlanCaps, setOriginalVlanCaps] = useState([]);

    // WAN Modal State (independent of VLAN — WAN caps track uplink usage)
    const [showWanModal, setShowWanModal] = useState(false);
    const [selectedDeviceForWan, setSelectedDeviceForWan] = useState(null);
    const [wanCaps, setWanCaps] = useState([]);
    const [wanLoading, setWanLoading] = useState(false);
    const [wanSaving, setWanSaving] = useState(false);
    const [originalWanCaps, setOriginalWanCaps] = useState([]);
    
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [showMobileAddDevice, setShowMobileAddDevice] = useState(false);
    const [dmActionDevice, setDmActionDevice] = useState(null); // mobile action sheet target (configured)
    const [dmOrphanedDevice, setDmOrphanedDevice] = useState(null); // mobile action sheet target (orphaned)
    const deviceSearchRef = useRef(null);

    // Guided tour: remembers the mobile add-panel state so it can be restored.
    const mobileAddBeforeTourRef = useRef(false);

    useEffect(() => {
        if (!showDeviceDropdown) return;
        const onClickOutside = (e) => {
            if (deviceSearchRef.current && !deviceSearchRef.current.contains(e.target)) {
                setShowDeviceDropdown(false);
            }
        };
        document.addEventListener('mousedown', onClickOutside);
        return () => document.removeEventListener('mousedown', onClickOutside);
    }, [showDeviceDropdown]);

    // NEW: Search state for configured devices
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
      const handleResize = () => {
        setIsMobile(window.innerWidth <= 768);
      };

      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        fetchDevices();
        fetchOrganizations();
    }, []);
    
    // Fetch alert status for all configured devices in a SINGLE request.
    // Backend returns only suppressed devices; missing = not suppressed (false).
    useEffect(() => {
        if (!devices.configured_devices || devices.configured_devices.length === 0) return;

        const fetchAllAlertStatus = async () => {
            try {
                const response = await axios.get('/api/admin/devices/alert-status');
                const list = Array.isArray(response.data) ? response.data : [];

                // Build lookup of suppressed serials
                const suppressedMap = {};
                for (const item of list) {
                    if (item && item.serial_number) {
                        suppressedMap[item.serial_number] = !!item.alerts_suppressed;
                    }
                }

                // Give every configured device an explicit value; missing = false.
                const nextStatus = {};
                for (const device of devices.configured_devices) {
                    if (device.serial_number) {
                        nextStatus[device.serial_number] = suppressedMap[device.serial_number] || false;
                    }
                }

                setDeviceAlertStatus(nextStatus);
            } catch (error) {
                console.error("Failed to fetch alert statuses:", error);
            }
        };

        fetchAllAlertStatus();
    }, [devices.configured_devices]);

    const showMessage = (text, type = 'success') => {
        setMessage({ text, type });
        setTimeout(() => setMessage({ text: '', type: '' }), 5000);
    };

    const fetchOrganizations = async () => {
        setLoadingOrgs(true);
        try {
            const response = await axios.get('/api/peplink/organizations');
            setOrganizations(response.data.organizations || []);
        } catch (error) {
            console.error('Error fetching organizations:', error);
            showMessage('Failed to fetch organizations', 'error');
        } finally {
            setLoadingOrgs(false);
        }
    };

    const getInterfaceIcon = (connType) => {
      const typeMap = {
        'ethernet': {
          icon: ethernetIcon,
          alt: 'Ethernet',
        },
        'cellular': {
          icon: cellularIcon,
          alt: 'Cellular',
        },
        'modem': {
          icon: modemIcon,
          alt: 'Modem',
        },
        'wireless': {
          icon: wifiIcon,
          alt: 'Wireless',
        },
        'wovlan': {
          icon: vlanIcon,
          alt: 'VLAN',
        },
        'wifi': {
          icon: wifiIcon,
          alt: 'WiFi',
        },
      };
      
      const config = typeMap[connType] || {
        icon: globeIcon,
        alt: 'Unknown',
      };
      
      return config;
    };


    const handleOrgChange = async (orgId) => {
        setSelectedOrg(orgId);
        setSelectedDevice('');
        setDeviceSearchQuery('');
        setShowDeviceDropdown(false);
        setOrgDevices([]);
        
        if (!orgId) return;
        
        setLoadingDevices(true);
        try {
            // Use the new endpoint that shows availability
            const response = await axios.get(`/api/peplink/organization/${orgId}/devices/available`);
            setOrgDevices(response.data.devices || []);
        } catch (error) {
            console.error('Error fetching devices:', error);
            showMessage('Failed to fetch devices for organization', 'error');
        } finally {
            setLoadingDevices(false);
        }
    };

    // const fetchAlertStatus = async (serialNumber) => {
    //     try {
    //         const response = await axios.get(`/api/admin/device/${serialNumber}/alert-status`);
    //         setDeviceAlertStatus(prev => ({
    //             ...prev,
    //             [serialNumber]: response.data.alerts_suppressed
    //         }));
    //     } catch (error) {
    //         console.error("Failed to fetch alert status:", error);
    //     }
    // };

    const handleToggleAlertSuppression = async (serialNumber, isSuppressed) => {
        const action = isSuppressed ? "unsuppress-alerts" : "suppress-alerts";
        const confirmText = isSuppressed
            ? "Re-enable alerts for this device?"
            : "Suppress alerts for this device?";

        if (!window.confirm(confirmText)) return;

        try {
            await axios.post(`/api/admin/device/${serialNumber}/${action}`);

            setDeviceAlertStatus(prev => ({
                ...prev,
                [serialNumber]: !isSuppressed
            }));

            showMessage(
                isSuppressed
                    ? "Alerts re-enabled successfully"
                    : "Alerts suppressed (maintenance mode)"
            );

        } catch (error) {
            showMessage("Failed to toggle alert suppression", "error");
            console.error('Error toggling alert suppression:', error);
        }
    };

    // ========== VLAN CAP MANAGEMENT FUNCTIONS ==========
    
    const handleOpenVlanModal = async (device) => {
        setSelectedDeviceForVlan(device);
        setShowVlanModal(true);
        setVlanLoading(true);
        
        try {
            // Fetch current VLAN caps for this device
            const response = await axios.get(`/api/vlan/caps/${device.serial_number}`);
            const caps = response.data.caps || [];
            setVlanCaps(caps);
            // Store original state for comparison
            setOriginalVlanCaps(JSON.parse(JSON.stringify(caps)));
        } catch (error) {
            console.error('Error fetching VLAN caps:', error);
            showMessage('Failed to load VLAN configuration', 'error');
        } finally {
            setVlanLoading(false);
        }
    };
    
    const handleVlanCapChange = (vlanId, field, value) => {
        setVlanCaps(prevCaps => 
            prevCaps.map(cap => {
                if (cap.vlan_id === vlanId) {
                    const updatedCap = { ...cap };
                    
                    if (field === 'has_cap') {
                        // Toggle cap on/off
                        if (!value) {
                            // Removing cap
                            updatedCap.has_cap = false;
                            updatedCap.max_cap_gb = null;
                            updatedCap.alert_percentage = 80;
                            updatedCap.alert_cap_gb = null;
                        } else {
                            // Enabling cap, set default values
                            updatedCap.has_cap = true;
                            updatedCap.max_cap_gb = updatedCap.max_cap_gb || 100;
                            updatedCap.alert_percentage = updatedCap.alert_percentage || 80;
                            updatedCap.alert_cap_gb = updatedCap.max_cap_gb * (updatedCap.alert_percentage / 100);
                        }
                    } 
                    else if (field === 'max_cap_gb') {
                        let maxCap = parseFloat(value) || 0;
                        // Ensure maxCap is not negative
                        maxCap = Math.max(0, maxCap);
                        updatedCap.max_cap_gb = maxCap;
                        
                        if (maxCap > 0) {
                            // Check if current alert cap is greater than new max cap
                            let currentAlertCap = updatedCap.alert_cap_gb || 0;
                            
                            if (currentAlertCap > maxCap) {
                                // If alert cap exceeds max cap, set alert cap to max cap and percentage to 100%
                                updatedCap.alert_cap_gb = maxCap;
                                updatedCap.alert_percentage = 100;
                            } else {
                                // Recalculate alert cap based on current percentage
                                updatedCap.alert_cap_gb = maxCap * (updatedCap.alert_percentage / 100);
                            }
                        } else {
                            updatedCap.alert_cap_gb = null;
                            updatedCap.alert_percentage = 80;
                        }
                    }
                    else if (field === 'alert_percentage') {
                        let percentage = parseFloat(value) || 0;
                        // Clamp percentage between 0 and 100
                        percentage = Math.min(100, Math.max(0, percentage));
                        updatedCap.alert_percentage = percentage;
                        
                        if (updatedCap.max_cap_gb > 0) {
                            let newAlertCap = updatedCap.max_cap_gb * (percentage / 100);
                            
                            // Ensure alert cap doesn't exceed max cap
                            if (newAlertCap > updatedCap.max_cap_gb) {
                                newAlertCap = updatedCap.max_cap_gb;
                                updatedCap.alert_percentage = 100;
                            }
                            updatedCap.alert_cap_gb = newAlertCap;
                        } else {
                            updatedCap.alert_cap_gb = null;
                        }
                    }
                    else if (field === 'alert_cap_gb') {
                        let alertCap = parseFloat(value) || 0;
                        // Ensure alert cap is not negative
                        alertCap = Math.max(0, alertCap);
                        
                        if (updatedCap.max_cap_gb > 0) {
                            // NEW LOGIC: If alert cap is greater than max cap, update max cap to match alert cap
                            if (alertCap > updatedCap.max_cap_gb) {
                                // Set max cap to alert cap value
                                updatedCap.max_cap_gb = alertCap;
                                // Percentage becomes 100%
                                updatedCap.alert_percentage = 100;
                                updatedCap.alert_cap_gb = alertCap;
                            } else {
                                // Normal case: alert cap is within bounds
                                updatedCap.alert_cap_gb = alertCap;
                                // Calculate percentage based on alert cap and max cap
                                const percentage = (alertCap / updatedCap.max_cap_gb) * 100;
                                updatedCap.alert_percentage = Math.min(100, Math.max(0, Math.round(percentage)));
                            }
                        } else {
                            // If max cap is 0 or not set, set max cap to alert cap
                            if (alertCap > 0) {
                                updatedCap.max_cap_gb = alertCap;
                                updatedCap.alert_percentage = 100;
                                updatedCap.alert_cap_gb = alertCap;
                            } else {
                                updatedCap.alert_cap_gb = null;
                            }
                        }
                    }
                    
                    // Final safety check: ensure alert cap never exceeds max cap
                    if (updatedCap.has_cap && updatedCap.max_cap_gb > 0 && updatedCap.alert_cap_gb > updatedCap.max_cap_gb) {
                        updatedCap.alert_cap_gb = updatedCap.max_cap_gb;
                        updatedCap.alert_percentage = 100;
                    }
                    
                    return updatedCap;
                }
                return cap;
            })
        );
    };

    const handleSaveVlanCaps = async () => {
        if (!selectedDeviceForVlan) return;
        
        // Validate all caps before saving
        for (const cap of vlanCaps) {
            if (cap.has_cap) {
                // Ensure max_cap_gb is positive
                if (!cap.max_cap_gb || cap.max_cap_gb <= 0) {
                    showMessage(`Please set a valid Max Cap for VLAN ${cap.vlan_id}`, 'error');
                    return;
                }
                
                // Ensure alert_cap_gb is valid and not greater than max_cap_gb
                if (cap.alert_cap_gb > cap.max_cap_gb) {
                    showMessage(`Alert Cap cannot exceed Max Cap for VLAN ${cap.vlan_id}`, 'error');
                    return;
                }
                
                // Ensure alert_percentage is within valid range
                if (cap.alert_percentage < 0 || cap.alert_percentage > 100) {
                    showMessage(`Alert percentage must be between 0 and 100 for VLAN ${cap.vlan_id}`, 'error');
                    return;
                }
            }
        }
        
        setVlanSaving(true);
        
        try {
            // Find VLANs that were enabled but now disabled (need deletion)
            const deletedVlanIds = [];
            const capsToSave = [];
            
            for (const currentCap of vlanCaps) {
                const originalCap = originalVlanCaps.find(oc => oc.vlan_id === currentCap.vlan_id);
                
                // Case 1: Cap was removed (currently has_cap false but originally had cap)
                if (!currentCap.has_cap && originalCap?.has_cap) {
                    deletedVlanIds.push(currentCap.vlan_id);
                }
                // Case 2: Cap is active and has valid values
                else if (currentCap.has_cap && currentCap.max_cap_gb > 0) {
                    // Final safety check before saving
                    let finalAlertCap = currentCap.alert_cap_gb;
                    let finalPercentage = currentCap.alert_percentage;
                    
                    // Ensure alert cap doesn't exceed max cap
                    if (finalAlertCap > currentCap.max_cap_gb) {
                        finalAlertCap = currentCap.max_cap_gb;
                        finalPercentage = 100;
                    }
                    
                    capsToSave.push({
                        vlan_id: currentCap.vlan_id,
                        max_cap_gb: currentCap.max_cap_gb,
                        alert_percentage: finalPercentage,
                    });
                }
            }
            
            // Send batch update with deletions
            await axios.post('/api/vlan/batch-caps', {
                serial_number: selectedDeviceForVlan.serial_number,
                caps: capsToSave,
                deleted_vlan_ids: deletedVlanIds
            });
            
            showMessage('VLAN caps saved successfully!');
            setShowVlanModal(false);
            
            // Refresh the devices list to update aggregated data
            fetchDevices();
            
        } catch (error) {
            console.error('Error saving VLAN caps:', error);
            showMessage('Failed to save VLAN configuration', 'error');
        } finally {
            setVlanSaving(false);
        }
    };
    
    // const handleRemoveVlanCap = async (vlanId) => {
    //     if (!selectedDeviceForVlan) return;
        
    //     if (!window.confirm(`Remove cap for this VLAN?`)) return;
        
    //     setVlanSaving(true);
        
    //     try {
    //         await axios.delete(`/api/vlan/cap/${selectedDeviceForVlan.serial_number}/${vlanId}`);
            
    //         // Update local state
    //         setVlanCaps(prevCaps => 
    //             prevCaps.map(cap => 
    //                 cap.vlan_id === vlanId 
    //                     ? { ...cap, has_cap: false, max_cap_gb: null, alert_cap_gb: null }
    //                     : cap
    //             )
    //         );
            
    //         showMessage('VLAN cap removed successfully!');
            
    //     } catch (error) {
    //         console.error('Error removing VLAN cap:', error);
    //         showMessage('Failed to remove VLAN cap', 'error');
    //     } finally {
    //         setVlanSaving(false);
    //     }
    // };
    
    // ========== END VLAN CAP MANAGEMENT ==========

    // ========== WAN CAP MANAGEMENT FUNCTIONS ==========
    // Mirrors the VLAN handlers. WAN caps are independent from VLAN caps: a WAN
    // is an internet uplink, a VLAN is a LAN segment, and the router routes any
    // VLAN's traffic through whichever WAN is active. So there is no linkage /
    // cross-validation between the two.

    const handleOpenWanModal = async (device) => {
        setSelectedDeviceForWan(device);
        setShowWanModal(true);
        setWanLoading(true);

        try {
            const response = await axios.get(`/api/wan/caps/${device.serial_number}`);
            const caps = response.data.caps || [];
            setWanCaps(caps);
            setOriginalWanCaps(JSON.parse(JSON.stringify(caps)));
        } catch (error) {
            console.error('Error fetching WAN caps:', error);
            showMessage('Failed to load WAN configuration', 'error');
        } finally {
            setWanLoading(false);
        }
    };

    const handleWanCapChange = (wanId, field, value) => {
        setWanCaps(prevCaps =>
            prevCaps.map(cap => {
                if (cap.wan_id === wanId) {
                    const updatedCap = { ...cap };

                    if (field === 'has_cap') {
                        if (!value) {
                            // Removing cap
                            updatedCap.has_cap = false;
                            updatedCap.max_cap_gb = null;
                            updatedCap.alert_percentage = 80;
                            updatedCap.alert_cap_gb = null;
                        } else {
                            // Enabling cap, set default values
                            updatedCap.has_cap = true;
                            updatedCap.max_cap_gb = updatedCap.max_cap_gb || 100;
                            updatedCap.alert_percentage = updatedCap.alert_percentage || 80;
                            updatedCap.alert_cap_gb = updatedCap.max_cap_gb * (updatedCap.alert_percentage / 100);
                        }
                    }
                    else if (field === 'max_cap_gb') {
                        let maxCap = parseFloat(value) || 0;
                        maxCap = Math.max(0, maxCap);
                        updatedCap.max_cap_gb = maxCap;

                        if (maxCap > 0) {
                            let currentAlertCap = updatedCap.alert_cap_gb || 0;
                            if (currentAlertCap > maxCap) {
                                updatedCap.alert_cap_gb = maxCap;
                                updatedCap.alert_percentage = 100;
                            } else {
                                updatedCap.alert_cap_gb = maxCap * (updatedCap.alert_percentage / 100);
                            }
                        } else {
                            updatedCap.alert_cap_gb = null;
                            updatedCap.alert_percentage = 80;
                        }
                    }
                    else if (field === 'alert_percentage') {
                        const percentage = parseInt(value) || 0;
                        updatedCap.alert_percentage = percentage;

                        if (updatedCap.max_cap_gb > 0) {
                            let newAlertCap = updatedCap.max_cap_gb * (percentage / 100);
                            if (newAlertCap > updatedCap.max_cap_gb) {
                                newAlertCap = updatedCap.max_cap_gb;
                                updatedCap.alert_percentage = 100;
                            }
                            updatedCap.alert_cap_gb = newAlertCap;
                        } else {
                            updatedCap.alert_cap_gb = null;
                        }
                    }
                    else if (field === 'alert_cap_gb') {
                        let alertCap = parseFloat(value) || 0;
                        alertCap = Math.max(0, alertCap);

                        if (updatedCap.max_cap_gb > 0) {
                            if (alertCap > updatedCap.max_cap_gb) {
                                // If alert cap exceeds max cap, bump max cap to match
                                updatedCap.max_cap_gb = alertCap;
                                updatedCap.alert_percentage = 100;
                                updatedCap.alert_cap_gb = alertCap;
                            } else {
                                updatedCap.alert_cap_gb = alertCap;
                                const percentage = (alertCap / updatedCap.max_cap_gb) * 100;
                                updatedCap.alert_percentage = Math.min(100, Math.max(0, Math.round(percentage)));
                            }
                        } else {
                            if (alertCap > 0) {
                                updatedCap.max_cap_gb = alertCap;
                                updatedCap.alert_cap_gb = alertCap;
                                updatedCap.alert_percentage = 100;
                            }
                        }
                    }

                    // Final safety check: alert cap never exceeds max cap
                    if (updatedCap.has_cap && updatedCap.max_cap_gb > 0 && updatedCap.alert_cap_gb > updatedCap.max_cap_gb) {
                        updatedCap.alert_cap_gb = updatedCap.max_cap_gb;
                        updatedCap.alert_percentage = 100;
                    }

                    return updatedCap;
                }
                return cap;
            })
        );
    };

    const handleSaveWanCaps = async () => {
        if (!selectedDeviceForWan) return;

        // Validate all caps before saving
        for (const cap of wanCaps) {
            if (cap.has_cap) {
                if (!cap.max_cap_gb || cap.max_cap_gb <= 0) {
                    showMessage(`Please set a valid Max Cap for ${cap.wan_name}`, 'error');
                    return;
                }
                if (cap.alert_cap_gb > cap.max_cap_gb) {
                    showMessage(`Alert Cap cannot exceed Max Cap for ${cap.wan_name}`, 'error');
                    return;
                }
                if (cap.alert_percentage < 0 || cap.alert_percentage > 100) {
                    showMessage(`Alert percentage must be between 0 and 100 for ${cap.wan_name}`, 'error');
                    return;
                }
            }
        }

        setWanSaving(true);

        try {
            const deletedWanIds = [];
            const capsToSave = [];

            for (const currentCap of wanCaps) {
                const originalCap = originalWanCaps.find(oc => oc.wan_id === currentCap.wan_id);

                if (!currentCap.has_cap && originalCap?.has_cap) {
                    deletedWanIds.push(currentCap.wan_id);
                }
                else if (currentCap.has_cap && currentCap.max_cap_gb > 0) {
                    let finalAlertCap = currentCap.alert_cap_gb;
                    let finalPercentage = currentCap.alert_percentage;

                    if (finalAlertCap > currentCap.max_cap_gb) {
                        finalAlertCap = currentCap.max_cap_gb;
                        finalPercentage = 100;
                    }

                    capsToSave.push({
                        wan_id: currentCap.wan_id,
                        max_cap_gb: currentCap.max_cap_gb,
                        alert_percentage: finalPercentage,
                    });
                }
            }

            await axios.post('/api/wan/batch-caps', {
                serial_number: selectedDeviceForWan.serial_number,
                caps: capsToSave,
                deleted_wan_ids: deletedWanIds
            });

            showMessage('WAN caps saved successfully!');
            setShowWanModal(false);
            fetchDevices();

        } catch (error) {
            console.error('Error saving WAN caps:', error);
            showMessage('Failed to save WAN configuration', 'error');
        } finally {
            setWanSaving(false);
        }
    };

    // ========== END WAN CAP MANAGEMENT ==========

    const handleReaddDevice = async (serial, orgId) => {
      if (!window.confirm(`Re-add device ${serial} to monitoring?`)) {
        return;
      }
      
      try {
        await axios.post('/api/devices/manage/add', {
          serial_number: serial,
          organization_id: orgId
        });
        fetchDevices();
        showMessage(`Device ${serial} re-added successfully!`);
      } catch (error) {
        showMessage(`Error: ${error.response?.data?.error || error.message}`, 'error');
      }
    };

    const formatOrphanedDateTime = (alertTime) => {
        if (!alertTime) return 'Unknown';

        const frenchDateTime = DateTime.fromISO(alertTime, { zone: 'Europe/Paris' });
        const utcDateTime = frenchDateTime.toUTC();

        return new Intl.DateTimeFormat('en-GB', {
          //timeZone: 'Europe/Paris',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false
        }).format(utcDateTime);
    };


    const formatDateTime = (timestamp) => {
        if (!timestamp) return 'Unknown';
        
        let date;
        
        if (typeof timestamp === 'number') {
          date = new Date(timestamp * 1000);
        } else {
          date = new Date(timestamp);
        }
        
        if (isNaN(date.getTime())) return 'Invalid date';
        
        const day = date.getDate();
        const month = date.toLocaleDateString('en-US', { month: 'short' });
        const year = date.getFullYear();
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        
        return `${day} ${month} ${year} ${hours}:${minutes}`;
    };
    
    
    const fetchDevices = async () => {
        try {
            const response = await axios.get('/api/devices/manage/list');
            setDevices(response.data);
            setLastUpdated(formatDateTime(new Date()));
        } catch (error) {
            console.error('Error fetching devices:', error);
            showMessage('Failed to fetch devices', 'error');
        } finally {
            setLoading(false);
        }
    };
    
    const handleAddDevice = async () => {
        if (!selectedOrg || !selectedDevice) {
            showMessage('Please select both Organization and Device', 'error');
            return;
        }
        
        // Check if device is already configured (client-side validation)
        const selectedDeviceData = orgDevices.find(d => d.sn === selectedDevice);
        if (selectedDeviceData?.is_configured) {
            showMessage('This device is already configured in the system', 'error');
            return;
        }
        
        setNewDevice({ ...newDevice, loading: true, error: null });
        
        try {
            await axios.post('/api/devices/manage/add', {
                serial_number: selectedDevice,
                organization_id: selectedOrg
            });
            
            setSelectedDevice('');
            fetchDevices();
            // Refresh device list to update availability
            if (selectedOrg) {
                handleOrgChange(selectedOrg);
            }
            showMessage('Device added successfully!');
        } catch (error) {
            const errorMessage = error.response?.data?.error || 
                                error.response?.data?.message || 
                                error.message;
            
            // Check for duplicate error
            if (error.response?.status === 409) {
                showMessage(`Device already exists: ${errorMessage}`, 'error');
            } else {
                showMessage(`Error adding device: ${errorMessage}`, 'error');
            }
            
            setNewDevice({ 
                ...newDevice, 
                error: errorMessage,
                loading: false 
            });
        }
    };

    const handleOpenBulkModal = async () => {
        if (!selectedOrg) {
            showMessage('Please select an organization first', 'error');
            return;
        }
        
        setBulkLoading(true);
        try {
            const response = await axios.get(`/api/peplink/organization/${selectedOrg}/devices/available`);
            const devices = response.data.devices || [];
            
            // Filter out already configured devices
            const availableDevices = devices.filter(d => !d.is_configured);
            
            if (availableDevices.length === 0) {
                showMessage('All devices in this organization are already configured', 'info');
                return;
            }
            
            setBulkDevices(availableDevices);
            
            // Initially select all available online devices
            const onlineSerials = new Set(
                availableDevices.filter(d => d.is_online).map(d => d.sn)
            );
            setSelectedBulkDevices(onlineSerials);
            setOnlyOnline(true);
            
            setShowBulkModal(true);
        } catch (error) {
            console.error('Error fetching devices for bulk add:', error);
            showMessage('Failed to fetch devices', 'error');
        } finally {
            setBulkLoading(false);
        }
    };

    const handleBulkDeviceToggle = (serial) => {
        const newSelected = new Set(selectedBulkDevices);
        if (newSelected.has(serial)) {
            newSelected.delete(serial);
        } else {
            newSelected.add(serial);
        }
        setSelectedBulkDevices(newSelected);
    };

    const handleSelectAllToggle = () => {
        if (selectedBulkDevices.size === filteredBulkDevices.length) {
            setSelectedBulkDevices(new Set());
        } else {
            setSelectedBulkDevices(new Set(filteredBulkDevices.map(d => d.sn)));
        }
    };

    const handleOnlyOnlineToggle = (checked) => {
        setOnlyOnline(checked);
        if (checked) {
            const onlineDevices = bulkDevices.filter(d => d.is_online).map(d => d.sn);
            setSelectedBulkDevices(new Set(onlineDevices));
        } else {
            setSelectedBulkDevices(new Set(bulkDevices.map(d => d.sn)));
        }
    };

    const handleBulkAddConfirm = async () => {
        // Use selectedOrg - make sure it's set
        if (!selectedOrg) {
            window.alert('Error', 'No organization selected');
            return;
        }
        
        const devicesToAdd = bulkDevices
            .filter(d => selectedBulkDevices.has(d.sn))
            .map(d => ({
                serial_number: d.sn,
                organization_id: selectedOrg
            }));
        
        if (devicesToAdd.length === 0) {
            window.alert('Error', 'No devices selected');
            return;
        }
        
        setBulkLoading(true);
        try {
            const response = await axios.post('/api/devices/manage/bulk-add', {
                devices: devicesToAdd
            });
            // Check if response indicates an error
            if (!response.success && response.error) {
                window.alert('Error', response.error);
                return;
            }
            
            setShowBulkModal(false);
            setShowEmailConfirm(false);
            setSelectedDevice('');
            fetchDevices();
            
            if (response.added_count === 0) {
                showMessage(
                    'Warning',
                    `No devices were added. ${response.failed_count} failed.\n\n` +
                    `Failed devices: ${response.failed_devices?.map(d => d.serial).join(', ') || 'Unknown'}`
                );
            } else {
                showMessage(
                    'Success',
                    `Successfully added ${response.added_count} devices` + 
                    (response.failed_count > 0 ? `\n${response.failed_count} failed` : '')
                );
            }
        } catch (error) {
            window.alert('Error', error.message || 'Failed to add devices');
        } finally {
            setBulkLoading(false);
        }
    };
    
    const handleRemoveDevice = async (serial, deleteData) => {
        if (!window.confirm(`Remove device ${serial}${deleteData ? ' and DELETE all data?' : ' from monitoring?'}`)) {
            return;
        }
        
        try {
            await axios.post('/api/devices/manage/remove', {
                serial_number: serial,
                delete_data: deleteData
            });
            fetchDevices();
            showMessage(`Device ${serial} removed${deleteData ? ' with data' : ''}`);
            
            setDeviceAlertStatus(prev => {
                const newState = { ...prev };
                delete newState[serial];
                return newState;
            });
        } catch (error) {
            showMessage(`Error: ${error.response?.data?.error || error.message}`, 'error');
        }
    };
    
    const handleCleanup = async (serial = null) => {
        const message = serial 
            ? `Delete archived data for ${serial}?`
            : 'Delete ALL archived data directories?';
        
        if (!window.confirm(message)) return;
        
        try {
            await axios.post('/api/devices/manage/cleanup', {
                serial_number: serial || undefined,
                delete_all: !serial
            });
            fetchDevices();
            showMessage('Cleanup completed!');
        } catch (error) {
            showMessage(`Error: ${error.response?.data?.error || error.message}`, 'error');
        }
    };
    
    const filteredBulkDevices = onlyOnline 
        ? bulkDevices.filter(d => d.is_online)
        : bulkDevices;

    // NEW: Filter configured devices based on search term
    const filteredConfiguredDevices = devices.configured_devices?.filter(device => {
        if (!searchTerm.trim()) return true;
        const searchLower = searchTerm.toLowerCase().trim();
        return (
            device.serial_number?.toLowerCase().includes(searchLower) ||
            device.device_name?.toLowerCase().includes(searchLower)
        );
    }) || [];

    // Guided tour ("?" in FleetHeader). Hands the tour this page's LIVE data.
    usePageTour('device-manager', {
        isMobile,
        configuredDevices: devices.configured_devices || [],
        orphanedDevices: devices.orphaned_devices || [],
        deviceAlertStatus,
        visibleDevices: filteredConfiguredDevices,
        openMobileAddPanel: () => setShowMobileAddDevice(true),
        closeMobileAddPanel: () => setShowMobileAddDevice(false),
        onTourStart: () => {
            // Close anything that would cover the page; remember UI state.
            setShowDeviceDropdown(false);
            setDmActionDevice(null);
            setDmOrphanedDevice(null);
            mobileAddBeforeTourRef.current = showMobileAddDevice;
        },
        onTourEnd: () => setShowMobileAddDevice(mobileAddBeforeTourRef.current),
    }, { ready: !loading });

    if (loading) return (
        <div className="cm-loading-overlay">
          <div className="cm-spinner-container">
            <video autoPlay loop muted playsInline preload="auto" poster={process.env.PUBLIC_URL + "/Logo_video_poster.jpg"} className="spinner-video" width="280" height="280">
              <source src={loadingVideo} type="video/mp4" />
            </video>
            <p>Loading devices…</p>
          </div>
        </div>
    );

    const handleBackToDashboard = () => {
        window.location.href = '/';
    };

    const handleLogout = async () => {
      if (!window.confirm('Are you sure you want to logout?')) return;
      if (onLogout) {
        onLogout();
        return;
      }
      try {
        await apiFetch('/api/auth/logout', { method: 'POST' });
        window.location.href = '/login';
      } catch (error) {
        console.error('Logout error:', error);
      }
    };
    
    return (
        <div className="App device-manager">
           <FleetHeader 
                title="Device Manager" 
                user={user}
                onLogout={handleLogout} 
            />
            
            <div className="dashboard-container">
                {message.text && (
                    <div className={`ft-toolbar-${message.type === 'error' ? 'error' : 'success'}`} role="alert">
                        <i className={`fas fa-${message.type === 'error' ? 'exclamation-triangle' : 'check-circle'} me-2`}></i>
                        {message.text}
                        <button type="button" className="btn-close" onClick={() => setMessage({ text: '', type: '' })}></button>
                    </div>
                )}

                {/* Add Device toolbar - flat, no card chrome, matching 3c */}
                <div className="dm-toolbar">
                    {isMobile && (
                        <button
                            className="cm-mobile-toggle"
                            data-tour="dm-mobile-add-toggle"
                            onClick={() => setShowMobileAddDevice((s) => !s)}
                        >
                            {showMobileAddDevice ? 'Hide' : '+ Add device'}
                        </button>
                    )}
                    {(!isMobile) && (
                        <div className="dm-toolbar-grid">
                            <div className="dm-toolbar-field dm-toolbar-org" data-tour="dm-org">
                                <label htmlFor="orgSelect">ORGANIZATION</label>
                                <select
                                    id="orgSelect"
                                    className="cm-input"
                                    value={selectedOrg}
                                    onChange={(e) => handleOrgChange(e.target.value)}
                                    disabled={loadingOrgs}
                                >
                                    {loadingOrgs ? (
                                        <option value="">Loading organizations...</option>
                                    ) : (
                                        <>
                                            <option value="">SELECT ORG…</option>
                                            {organizations.map(org => (
                                                <option key={org.id} value={org.id}>
                                                    {org.display_name}
                                                </option>
                                            ))}
                                        </>
                                    )}
                                </select>
                            </div>
                            
                            <div className="dm-toolbar-field dm-toolbar-device" ref={deviceSearchRef} style={{ position: 'relative' }} data-tour="dm-device">
                                <label htmlFor="deviceSearch">DEVICE</label>
                                <input
                                    id="deviceSearch"
                                    type="text"
                                    className="cm-input"
                                    placeholder={!selectedOrg ? 'select an organization first' : loadingDevices ? 'loading devices…' : 'search by name or serial number…'}
                                    disabled={!selectedOrg || loadingDevices}
                                    value={
                                        showDeviceDropdown
                                            ? deviceSearchQuery
                                            : (orgDevices.find(d => d.sn === selectedDevice)?.display_name || deviceSearchQuery)
                                    }
                                    onChange={(e) => {
                                        setDeviceSearchQuery(e.target.value);
                                        setSelectedDevice('');
                                        setShowDeviceDropdown(true);
                                    }}
                                    onFocus={() => setShowDeviceDropdown(true)}
                                />

                                {showDeviceDropdown && !loadingDevices && (
                                    <div className="dm-device-dropdown">
                                        {orgDevices
                                            .filter(device => {
                                                const q = deviceSearchQuery.trim().toLowerCase();
                                                if (!q) return true;
                                                return (
                                                    (device.display_name || '').toLowerCase().includes(q) ||
                                                    (device.sn || '').toLowerCase().includes(q)
                                                );
                                            })
                                            .map(device => (
                                                <div
                                                    key={device.id}
                                                    className={'dm-device-option' + (device.is_configured ? ' disabled' : '')}
                                                    onClick={() => {
                                                        if (device.is_configured) return;
                                                        setSelectedDevice(device.sn);
                                                        setDeviceSearchQuery('');
                                                        setShowDeviceDropdown(false);
                                                    }}
                                                >
                                                    <span className={'dm-device-dot ' + (device.is_online ? 'online' : 'offline')} />
                                                    <span className="dm-device-option-name">{device.display_name}</span>
                                                    {device.is_configured && <span className="dm-device-tag">Already Added</span>}
                                                </div>
                                            ))}
                                        {orgDevices.filter(device => {
                                            const q = deviceSearchQuery.trim().toLowerCase();
                                            if (!q) return true;
                                            return (
                                                (device.display_name || '').toLowerCase().includes(q) ||
                                                (device.sn || '').toLowerCase().includes(q)
                                            );
                                        }).length === 0 && (
                                            <div className="dm-device-option-empty">No matching devices</div>
                                        )}
                                    </div>
                                )}
                            </div>
                            
                            <button 
                                className="dm-toolbar-btn"
                                data-tour="dm-add-btn"
                                onClick={handleAddDevice}
                                disabled={!selectedOrg || !selectedDevice || newDevice.loading}
                            >
                                {newDevice.loading ? 'Adding...' : 'Add device'}
                            </button>
                            
                            <button 
                                className="dm-toolbar-btn"
                                data-tour="dm-bulk-btn"
                                onClick={handleOpenBulkModal}
                                disabled={!selectedOrg || bulkLoading}
                            >
                                Bulk add
                            </button>

                            {newDevice.error && (
                                <div className="ft-toolbar-error">
                                    {newDevice.error}
                                </div>
                            )}
                        </div>
                    )}

                    {(isMobile && showMobileAddDevice) && (
                        <div className="dm-toolbar-grid" data-tour="dm-add-form">
                            <div className="dm-toolbar-field dm-toolbar-org">
                                <label htmlFor="orgSelect">ORGANIZATION</label>
                                <select
                                    id="orgSelect"
                                    className="cm-input"
                                    value={selectedOrg}
                                    onChange={(e) => handleOrgChange(e.target.value)}
                                    disabled={loadingOrgs}
                                >
                                    {loadingOrgs ? (
                                        <option value="">Loading organizations...</option>
                                    ) : (
                                        <>
                                            <option value="">SELECT ORG…</option>
                                            {organizations.map(org => (
                                                <option key={org.id} value={org.id}>
                                                    {org.display_name}
                                                </option>
                                            ))}
                                        </>
                                    )}
                                </select>
                            </div>

                            <button 
                                className="dm-toolbar-btn"
                                onClick={handleOpenBulkModal}
                                disabled={!selectedOrg || bulkLoading}
                            >
                                Bulk add
                            </button>
                            
                            <div className="dm-toolbar-field dm-toolbar-device" ref={deviceSearchRef} style={{ position: 'relative' }}>
                                <label htmlFor="deviceSearch">DEVICE</label>
                                <input
                                    id="deviceSearch"
                                    type="text"
                                    className="cm-input"
                                    placeholder={!selectedOrg ? 'select an organization first' : loadingDevices ? 'loading devices…' : 'search by name or serial number…'}
                                    disabled={!selectedOrg || loadingDevices}
                                    value={
                                        showDeviceDropdown
                                            ? deviceSearchQuery
                                            : (orgDevices.find(d => d.sn === selectedDevice)?.display_name || deviceSearchQuery)
                                    }
                                    onChange={(e) => {
                                        setDeviceSearchQuery(e.target.value);
                                        setSelectedDevice('');
                                        setShowDeviceDropdown(true);
                                    }}
                                    onFocus={() => setShowDeviceDropdown(true)}
                                />

                                {showDeviceDropdown && !loadingDevices && (
                                    <div className="dm-device-dropdown">
                                        {orgDevices
                                            .filter(device => {
                                                const q = deviceSearchQuery.trim().toLowerCase();
                                                if (!q) return true;
                                                return (
                                                    (device.display_name || '').toLowerCase().includes(q) ||
                                                    (device.sn || '').toLowerCase().includes(q)
                                                );
                                            })
                                            .map(device => (
                                                <div
                                                    key={device.id}
                                                    className={'dm-device-option' + (device.is_configured ? ' disabled' : '')}
                                                    onClick={() => {
                                                        if (device.is_configured) return;
                                                        setSelectedDevice(device.sn);
                                                        setDeviceSearchQuery('');
                                                        setShowDeviceDropdown(false);
                                                    }}
                                                >
                                                    <span className={'dm-device-dot ' + (device.is_online ? 'online' : 'offline')} />
                                                    <span className="dm-device-option-name">{device.display_name}</span>
                                                    {device.is_configured && <span className="dm-device-tag">Already Added</span>}
                                                </div>
                                            ))}
                                        {orgDevices.filter(device => {
                                            const q = deviceSearchQuery.trim().toLowerCase();
                                            if (!q) return true;
                                            return (
                                                (device.display_name || '').toLowerCase().includes(q) ||
                                                (device.sn || '').toLowerCase().includes(q)
                                            );
                                        }).length === 0 && (
                                            <div className="dm-device-option-empty">No matching devices</div>
                                        )}
                                    </div>
                                )}
                            </div>
                            
                            <button 
                                className="dm-toolbar-btn"
                                onClick={handleAddDevice}
                                disabled={!selectedOrg || !selectedDevice || newDevice.loading}
                            >
                                {newDevice.loading ? 'Adding...' : 'Add device'}
                            </button>

                            {newDevice.error && (
                                <div className="ft-toolbar-error">
                                    {newDevice.error}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {showBulkModal && (
                    <div className="modal-overlay">
                        <div className="modal-content bulk-modal">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    BULK ADD
                                    <span className="modal-title-sep">/</span>
                                    <span className="modal-title-entity">{organizations.find(o => o.id === selectedOrg)?.name}</span>
                                </h5>
                                <button 
                                    className="btn-close" 
                                    onClick={() => {
                                        setShowBulkModal(false);
                                        setShowEmailConfirm(false);
                                    }}
                                >
                                </button>
                            </div>
                            <div className="device-manager-modal-body">
                                {!showEmailConfirm ? (
                                    <>
                                        <div className="bulk-stat-row">
                                            <div className="bulk-stat">
                                                <span className="bulk-stat-value">{bulkDevices.length}</span>
                                                <span className="bulk-stat-label">TOTAL</span>
                                            </div>
                                            <div className="bulk-stat">
                                                <span className="bulk-stat-value accent">{bulkDevices.filter(d => d.is_online).length}</span>
                                                <span className="bulk-stat-label">ONLINE</span>
                                            </div>
                                            <div className="bulk-stat">
                                                <span className="bulk-stat-value dim">{bulkDevices.filter(d => !d.is_online).length}</span>
                                                <span className="bulk-stat-label">OFFLINE</span>
                                            </div>
                                            <div className="bulk-stat">
                                                <span className="bulk-stat-value accent">{selectedBulkDevices.size}</span>
                                                <span className="bulk-stat-label">SELECTED</span>
                                            </div>
                                        </div>

                                        <div className="bulk-controls">
                                            <label className="bulk-checkbox">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedBulkDevices.size === filteredBulkDevices.length && filteredBulkDevices.length > 0}
                                                    onChange={handleSelectAllToggle}
                                                />
                                                <span>SELECT ALL ({selectedBulkDevices.size}/{filteredBulkDevices.length})</span>
                                            </label>
                                            <label className="bulk-checkbox">
                                                <input
                                                    type="checkbox"
                                                    checked={onlyOnline}
                                                    onChange={(e) => handleOnlyOnlineToggle(e.target.checked)}
                                                />
                                                <span>ONLY ONLINE</span>
                                            </label>
                                        </div>
                                        
                                        <div className="cm-grid-wrap bulk-grid-wrap">
                                            <div className="dm-grid-header bulk-grid-header">
                                                <div></div>
                                                <div>STATUS</div>
                                                <div>SERIAL</div>
                                                <div>NAME</div>
                                                <div>PRODUCT</div>
                                                <div>FIRMWARE</div>
                                            </div>
                                            {filteredBulkDevices.map((device, idx) => (
                                                <div
                                                    key={device.id}
                                                    className={'dm-grid-row bulk-grid-row' + (idx % 2 === 1 ? ' cm-grid-row-alt' : '') + (!device.is_online ? ' dim-row' : '')}
                                                    onClick={() => handleBulkDeviceToggle(device.sn)}
                                                >
                                                    <div>
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedBulkDevices.has(device.sn)}
                                                            onChange={() => handleBulkDeviceToggle(device.sn)}
                                                            onClick={(e) => e.stopPropagation()}
                                                        />
                                                    </div>
                                                    <div>
                                                        <span className={'cap-dot' + (device.is_online ? ' online' : '')} />
                                                    </div>
                                                    <div className="cm-mono">{device.sn}</div>
                                                    <div className="cm-client-name">{device.name}</div>
                                                    <div className="cm-detail-meta">{device.product_name}</div>
                                                    <div className="cm-detail-meta">{device.fw_ver || 'N/A'}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </>
                                ) : (
                                    <div className="bulk-confirm">
                                        <h6>CONFIRM ADD</h6>
                                        <p className="text-muted">
                                            You are about to add <strong>{selectedBulkDevices.size}</strong> devices to monitoring.
                                            Do you want to continue?
                                        </p>
                                    </div>
                                )}
                            </div>
                            <div className="modal-footer">
                                {!showEmailConfirm ? (
                                    <>
                                        <button 
                                            className="btn btn-secondary" 
                                            onClick={() => setShowBulkModal(false)}
                                        >
                                            Cancel
                                        </button>
                                        <button 
                                            className="btn btn-primary"
                                            onClick={() => setShowEmailConfirm(true)}
                                            disabled={selectedBulkDevices.size === 0}
                                        >
                                            Continue with {selectedBulkDevices.size} devices
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <button 
                                            className="btn btn-secondary" 
                                            onClick={() => setShowEmailConfirm(false)}
                                        >
                                            Back
                                        </button>
                                        <button 
                                            className="btn btn-primary"
                                            onClick={handleBulkAddConfirm}
                                            disabled={bulkLoading}
                                        >
                                            {bulkLoading ? (
                                                <>
                                                    <i className="fas fa-spinner fa-spin me-2"></i>
                                                    Adding devices...
                                                </>
                                            ) : (
                                                `Confirm add ${selectedBulkDevices.size} devices`
                                            )}
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Configured Devices Table - Keep existing code */}
                <div className="cm-panel" style={{ marginBottom: '2rem' }}>
                    <div className="dm-stats-bar" data-tour="dm-configured-header">
                        <span className="dm-stats-label">CONFIGURED DEVICES</span>
                        <span className="dm-stats-count">{filteredConfiguredDevices.length} / {devices.configured_devices?.length || 0}</span>
                        <div className="dm-search-box">
                            <i className="fas fa-search"></i>
                            <input
                                type="text"
                                placeholder="device name or serial"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="cm-panel-body" style={{ padding: 0 }}>
                    {filteredConfiguredDevices.length === 0 ? (
                        <div className="text-center py-4">
                            <div className="empty-state">
                                {searchTerm ? (
                                    <>
                                        <h5>No matching devices found</h5>
                                        <p className="text-muted">
                                            No devices match "{searchTerm}"
                                        </p>
                                        <button 
                                            className="btn btn-sm btn-outline-secondary mt-2"
                                            onClick={() => setSearchTerm('')}
                                        >
                                            Clear Search
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <h5>No configured devices</h5>
                                        <p className="text-muted">Add a device to start monitoring</p>
                                    </>
                                )}
                            </div>
                        </div>
                    ) : isMobile ? (
                                        <div className="dm-mobile-list">
                                            {filteredConfiguredDevices.map((device, idx) => (
                                                <div key={device.serial_number} className="dm-card-4c" data-tour={idx === 0 ? 'dm-first-device' : undefined}>
                                                    <div className="dm-card-4c-top">
                                                        <span className="dm-card-4c-name">{device.device_name || 'Unnamed Device'}</span>
                                                        {deviceAlertStatus[device.serial_number] ? (
                                                            <span className="cm-status-chip cm-inactive">SUPPRESSED</span>
                                                        ) : (
                                                            <span className="cm-status-chip cm-active">ACTIVE</span>
                                                        )}
                                                    </div>
                                                    <div className="dm-card-4c-meta">
                                                        {device.serial_number} · {device.organization_id}
                                                    </div>
                                                    <div className="dm-card-4c-actions" data-tour={idx === 0 ? 'dm-first-actions' : undefined}>
                                                        <button className="dm-card-4c-btn" onClick={() => handleOpenVlanModal(device)}>VLAN CAPS</button>
                                                        <button className="dm-card-4c-btn" onClick={() => handleOpenWanModal(device)}>WAN CAPS</button>
                                                        <button
                                                            className="dm-card-4c-btn"
                                                            style={{ color: '#ffffff80' }}
                                                            onClick={() => handleToggleAlertSuppression(device.serial_number, deviceAlertStatus[device.serial_number])}
                                                        >
                                                            {deviceAlertStatus[device.serial_number] ? 'UNMUTE' : 'MUTE'}
                                                        </button>
                                                        <button
                                                            className="dm-card-4c-btn"
                                                            style={{ color: '#ffffff80' }}
                                                            onClick={() => handleRemoveDevice(device.serial_number, false)}
                                                        >
                                                            STOP
                                                        </button>
                                                        <button
                                                            className="dm-card-4c-btn"
                                                            style={{ color: '#cafb6d' }}
                                                            onClick={() => handleRemoveDevice(device.serial_number, true)}
                                                        >
                                                            DELETE
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="cm-grid-wrap">
                                            <div className="dm-grid-header">
                                                <div>SERIAL</div>
                                                <div>NAME</div>
                                                <div>ORG</div>
                                                <div>DATA DIR</div>
                                                <div>ALERTS</div>
                                                <div className="cm-num">ACTIONS</div>
                                            </div>
                                            {filteredConfiguredDevices.map((device, idx) => (
                                                <div key={device.serial_number} className={'dm-grid-row' + (idx % 2 === 1 ? ' cm-grid-row-alt' : '')} data-tour={idx === 0 ? 'dm-first-device' : undefined}>
                                                    <div className="cm-mono">{device.serial_number}</div>
                                                    <div className="cm-client-name">{device.device_name}</div>
                                                    <div className="cm-detail-meta">{device.organization_id}</div>
                                                    <div>
                                                        <span className={'cm-status-chip ' + (device.has_directory ? 'cm-active' : 'cm-inactive')}>
                                                            {device.has_directory ? 'PRESENT' : 'MISSING'}
                                                        </span>
                                                    </div>
                                                    <div>
                                                        <span className={'cm-status-chip ' + (
                                                            deviceAlertStatus[device.serial_number] === undefined ? 'cm-pending' :
                                                            deviceAlertStatus[device.serial_number] ? 'cm-inactive' : 'cm-active'
                                                        )}>
                                                            {deviceAlertStatus[device.serial_number] === undefined ? 'UNKNOWN' :
                                                             deviceAlertStatus[device.serial_number] ? 'SUPPRESSED' : 'ACTIVE'}
                                                        </span>
                                                    </div>
                                                    <div className="cm-row-actions" data-tour={idx === 0 ? 'dm-first-actions' : undefined}>
                                                        <button className="cm-action" onClick={() => handleOpenVlanModal(device)}>VLAN CAPS</button>
                                                        <button className="cm-action" onClick={() => handleOpenWanModal(device)}>WAN CAPS</button>
                                                        <button
                                                            className="cm-action cm-action-muted"
                                                            onClick={() => handleToggleAlertSuppression(device.serial_number, deviceAlertStatus[device.serial_number])}
                                                        >
                                                            {deviceAlertStatus[device.serial_number] ? 'UNMUTE' : 'MUTE'}
                                                        </button>
                                                        <button
                                                            className="cm-action cm-action-muted"
                                                            onClick={() => handleRemoveDevice(device.serial_number, false)}
                                                        >
                                                            STOP MONITORING
                                                        </button>
                                                        <button
                                                            className="cm-action"
                                                            style={{ color: '#cafb6d' }}
                                                            onClick={() => handleRemoveDevice(device.serial_number, true)}
                                                        >
                                                            STOP &amp; REMOVE DATA
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                    </div>
                </div>

                {/* Archived Data Directories (orphaned devices) */}
                <div className="cm-panel" data-tour="dm-archived">
                    <div className="dm-stats-bar">
                        <span className="dm-stats-label">ARCHIVED DATA DIRECTORIES</span>
                        <span className="dm-stats-count">{devices.orphaned_devices?.length || 0}</span>
                        {devices.orphaned_devices?.length > 0 && (
                            <button
                                className="cm-action cm-action-muted"
                                onClick={() => handleCleanup()}
                                title="Delete all archived data directories"
                                style={{ marginLeft: 'auto' }}
                            >
                                DELETE ALL ARCHIVED DATA
                            </button>
                        )}
                    </div>
                    <div className="cm-panel-body" style={{ padding: 0 }}>
                        {devices.orphaned_devices?.length === 0 || !devices.orphaned_devices ? (
                            <div className="text-center py-4">
                                <div className="empty-state">
                                    <h5>No archived data</h5>
                                    <p className="text-muted">
                                        Data directories left behind by removed devices will appear here.
                                    </p>
                                </div>
                            </div>
                        ) : isMobile ? (
                            <div className="dm-mobile-list">
                                {devices.orphaned_devices.map(device => (
                                    <div key={device.serial_number} className="dm-card-4c">
                                        <div className="dm-card-4c-top">
                                            <span className="dm-card-4c-name">{device.device_name || '—'}</span>
                                            <span className="cm-status-chip cm-inactive">{device.data_size || '0 KB'}</span>
                                        </div>
                                        <div className="dm-card-4c-meta">
                                            {device.serial_number} · {device.organization_id || '—'} · {formatOrphanedDateTime(device.last_update)}
                                        </div>
                                        <div className="dm-card-4c-actions">
                                            <button
                                                className="dm-card-4c-btn"
                                                onClick={() => handleReaddDevice(device.serial_number, device.organization_id)}
                                                disabled={!device.organization_id || device.organization_id === 'Unknown'}
                                            >
                                                RE-ADD
                                            </button>
                                            <button className="dm-card-4c-more" onClick={() => setDmOrphanedDevice(device)}>⋯</button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="cm-grid-wrap">
                                <div className="dm-grid-header dm-grid-header-archived">
                                    <div>SERIAL</div>
                                    <div>NAME</div>
                                    <div>ORG</div>
                                    <div>LAST UPDATE</div>
                                    <div>DATA SIZE</div>
                                    <div className="cm-num">ACTIONS</div>
                                </div>
                                {devices.orphaned_devices.map((device, idx) => (
                                    <div key={device.serial_number} className={'dm-grid-row dm-grid-row-archived' + (idx % 2 === 1 ? ' cm-grid-row-alt' : '')}>
                                        <div className="cm-mono">{device.serial_number}</div>
                                        <div className="cm-client-name">{device.device_name || '—'}</div>
                                        <div className="cm-detail-meta">{device.organization_id || '—'}</div>
                                        <div className="cm-detail-meta">{formatOrphanedDateTime(device.last_update)}</div>
                                        <div><span className="cm-status-chip cm-inactive">{device.data_size || '0 KB'}</span></div>
                                        <div className="cm-row-actions">
                                            <button
                                                className="cm-action"
                                                onClick={() => handleReaddDevice(device.serial_number, device.organization_id)}
                                                disabled={!device.organization_id || device.organization_id === 'Unknown'}
                                            >
                                                RE-ADD
                                            </button>
                                            <button
                                                className="cm-action cm-action-muted"
                                                onClick={() => handleCleanup(device.serial_number)}
                                            >
                                                DELETE DATA
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* VLAN CAP MANAGEMENT MODAL - matches mockup 3f exactly */}
            {showVlanModal && selectedDeviceForVlan && (
                <div className="modal-overlay">
                    <div className="modal-content cap-modal">
                        <div className="modal-header">
                            <h5 className="modal-title">
                                VLAN CAPS
                                <span className="modal-title-sep">/</span>
                                <span className="modal-title-entity">{selectedDeviceForVlan.device_name}</span>
                            </h5>
                            <button 
                                className="btn-close" 
                                onClick={() => setShowVlanModal(false)}
                            >
                            </button>
                        </div>

                        <div className="cap-modal-info">
                            At the alert threshold both client and admin are notified. At the max cap only admins are — manual intervention.
                        </div>

                        <div className="modal-body" style={{ padding: 0, minHeight: '400px' }}>
                            {vlanLoading ? (
                                <div className="text-center py-5">
                                    <div className="spinner"></div>
                                    <p className="mt-3">Loading VLAN configuration...</p>
                                </div>
                            ) : vlanCaps.length === 0 ? (
                                <div className="text-center py-5">
                                    <p className="text-muted">No VLAN interfaces found for this device.</p>
                                </div>
                            ) : (
                                <>
                                    <div className="cap-grid-header">
                                        <div>VLAN</div>
                                        <div className="cm-num">USED GB</div>
                                        <div className="cm-num">ALERT AT</div>
                                        <div className="cm-num">MAX CAP</div>
                                        <div className="cm-num">CAP</div>
                                    </div>
                                    {vlanCaps.map((vlan) => (
                                        <div key={vlan.vlan_id} className={'cap-grid-row' + (vlan.has_cap ? ' capped' : '')}>
                                            <div>
                                                <div className="cap-row-name">{vlan.vlan_name}</div>
                                                <div className="cap-row-sub">VLAN {vlan.vlan_id}</div>
                                            </div>
                                            <div className={
                                                'cm-num cap-usage' +
                                                (vlan.has_cap && vlan.current_usage_gb > 0 ? ' accent' : '') +
                                                (!vlan.current_usage_gb ? ' dim' : '')
                                            }>
                                                {(vlan.current_usage_gb || 0).toFixed(2)}
                                            </div>
                                            <div className="cm-num">
                                                {vlan.has_cap ? (
                                                    <input
                                                        type="number"
                                                        className="cap-inline-input"
                                                        value={vlan.alert_cap_gb || ''}
                                                        onChange={(e) => handleVlanCapChange(vlan.vlan_id, 'alert_cap_gb', e.target.value)}
                                                        placeholder="—"
                                                        step="10"
                                                        min="0"
                                                    />
                                                ) : (
                                                    <span className="cap-dash">—</span>
                                                )}
                                            </div>
                                            <div className="cm-num">
                                                {vlan.has_cap ? (
                                                    <input
                                                        type="number"
                                                        className="cap-inline-input"
                                                        value={vlan.max_cap_gb || ''}
                                                        onChange={(e) => handleVlanCapChange(vlan.vlan_id, 'max_cap_gb', e.target.value)}
                                                        placeholder="—"
                                                        step="10"
                                                        min="0"
                                                    />
                                                ) : (
                                                    <span className="cap-dash">—</span>
                                                )}
                                            </div>
                                            <div className="cap-toggle-cell">
                                                <label className="cap-toggle">
                                                    <input
                                                        type="checkbox"
                                                        checked={vlan.has_cap || false}
                                                        onChange={(e) => handleVlanCapChange(vlan.vlan_id, 'has_cap', e.target.checked)}
                                                    />
                                                    <span className={'cap-toggle-slider' + (vlan.has_cap ? ' on' : '')}>
                                                        <span className="cap-toggle-knob" />
                                                    </span>
                                                </label>
                                            </div>
                                        </div>
                                    ))}
                                </>
                            )}
                        </div>
                        <div className="modal-footer cap-modal-footer">
                            <span className="cap-modal-footer-note">
                                {vlanCaps.filter((v) => v.has_cap).length} OF {vlanCaps.length} CAPPED
                            </span>
                            <button 
                                className="btn btn-secondary" 
                                onClick={() => setShowVlanModal(false)}
                            >
                                Cancel
                            </button>
                            <button 
                                className="btn btn-primary"
                                onClick={handleSaveVlanCaps}
                                disabled={vlanSaving || vlanLoading}
                            >
                                {vlanSaving ? (
                                    <>
                                        <i className="fas fa-spinner fa-spin me-2"></i>
                                        Saving...
                                    </>
                                ) : (
                                    'Save VLAN caps'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* WAN CAP MANAGEMENT MODAL - matches mockup 3f exactly */}
            {showWanModal && selectedDeviceForWan && (
                <div className="modal-overlay">
                    <div className="modal-content cap-modal">
                        <div className="modal-header">
                            <h5 className="modal-title">
                                WAN CAPS
                                <span className="modal-title-sep">/</span>
                                <span className="modal-title-entity">{selectedDeviceForWan.device_name}</span>
                            </h5>
                            <button
                                className="btn-close"
                                onClick={() => setShowWanModal(false)}
                            >
                            </button>
                        </div>

                        <div className="cap-modal-info">
                            Monthly caps per uplink. Same escalation as VLAN caps — client at threshold, admin only at max.
                        </div>

                        <div className="modal-body" style={{ padding: 0 }}>
                            {wanLoading ? (
                                <div className="text-center py-5">
                                    <div className="spinner"></div>
                                    <p className="mt-3">Loading WAN configuration...</p>
                                </div>
                            ) : wanCaps.length === 0 ? (
                                <div className="text-center py-5">
                                    <p className="text-muted">No WAN interfaces found for this device.</p>
                                </div>
                            ) : (
                                <>
                                    <div className="cap-grid-header cap-grid-header-wan">
                                        <div>UPLINK</div>
                                        <div className="cm-num">MONTH GB</div>
                                        <div className="cm-num">ALERT AT</div>
                                        <div className="cm-num">MAX CAP</div>
                                        <div className="cm-num">CAP</div>
                                    </div>
                                    {wanCaps.map((wan) => (
                                        <div key={wan.wan_id} className={'cap-grid-row cap-grid-row-wan' + (wan.has_cap ? ' capped' : '')}>
                                            <div className="cap-row-name-dot">
                                                <span className={'cap-dot' + (wan.is_online ? ' online' : '')} />
                                                <span className="cap-row-name">{wan.wan_name}</span>
                                            </div>
                                            <div className={
                                                'cm-num cap-usage' +
                                                (wan.has_cap && wan.current_usage_gb > 0 ? ' accent' : '') +
                                                (!wan.current_usage_gb ? ' dim' : '')
                                            }>
                                                {(wan.current_usage_gb || 0).toFixed(2)}
                                            </div>
                                            <div className="cm-num">
                                                {wan.has_cap ? (
                                                    <input
                                                        type="number"
                                                        className="cap-inline-input"
                                                        value={wan.alert_cap_gb || ''}
                                                        onChange={(e) => handleWanCapChange(wan.wan_id, 'alert_cap_gb', e.target.value)}
                                                        placeholder="—"
                                                        step="10"
                                                        min="0"
                                                    />
                                                ) : (
                                                    <span className="cap-dash">—</span>
                                                )}
                                            </div>
                                            <div className="cm-num">
                                                {wan.has_cap ? (
                                                    <input
                                                        type="number"
                                                        className="cap-inline-input"
                                                        value={wan.max_cap_gb || ''}
                                                        onChange={(e) => handleWanCapChange(wan.wan_id, 'max_cap_gb', e.target.value)}
                                                        placeholder="—"
                                                        step="10"
                                                        min="0"
                                                    />
                                                ) : (
                                                    <span className="cap-dash">—</span>
                                                )}
                                            </div>
                                            <div className="cap-toggle-cell">
                                                <label className="cap-toggle">
                                                    <input
                                                        type="checkbox"
                                                        checked={wan.has_cap || false}
                                                        onChange={(e) => handleWanCapChange(wan.wan_id, 'has_cap', e.target.checked)}
                                                    />
                                                    <span className={'cap-toggle-slider' + (wan.has_cap ? ' on' : '')}>
                                                        <span className="cap-toggle-knob" />
                                                    </span>
                                                </label>
                                            </div>
                                        </div>
                                    ))}
                                </>
                            )}
                        </div>
                        <div className="modal-footer cap-modal-footer">
                            <span className="cap-modal-footer-note">
                                {wanCaps.filter((w) => w.has_cap).length} OF {wanCaps.length} CAPPED
                            </span>
                            <button 
                                className="btn btn-secondary" 
                                onClick={() => setShowWanModal(false)}
                            >
                                Cancel
                            </button>
                            <button 
                                className="btn btn-primary"
                                onClick={handleSaveWanCaps}
                                disabled={wanSaving || wanLoading}
                            >
                                {wanSaving ? (
                                    <>
                                        <i className="fas fa-spinner fa-spin me-2"></i>
                                        Saving...
                                    </>
                                ) : (
                                    'Save WAN caps'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {dmActionDevice && (
                <DmActionSheet
                    device={dmActionDevice}
                    isSuppressed={deviceAlertStatus[dmActionDevice.serial_number]}
                    onClose={() => setDmActionDevice(null)}
                    onVlanCaps={() => { setDmActionDevice(null); handleOpenVlanModal(dmActionDevice); }}
                    onWanCaps={() => { setDmActionDevice(null); handleOpenWanModal(dmActionDevice); }}
                    onToggleAlerts={() => {
                        setDmActionDevice(null);
                        handleToggleAlertSuppression(dmActionDevice.serial_number, deviceAlertStatus[dmActionDevice.serial_number]);
                    }}
                    onRemoveMonitoring={() => { setDmActionDevice(null); handleRemoveDevice(dmActionDevice.serial_number, false); }}
                    onDeleteData={() => { setDmActionDevice(null); handleRemoveDevice(dmActionDevice.serial_number, true); }}
                />
            )}

            {dmOrphanedDevice && (
                <DmOrphanedActionSheet
                    device={dmOrphanedDevice}
                    onClose={() => setDmOrphanedDevice(null)}
                    onReadd={() => {
                        setDmOrphanedDevice(null);
                        handleReaddDevice(dmOrphanedDevice.serial_number, dmOrphanedDevice.organization_id);
                    }}
                    onDeleteData={() => {
                        setDmOrphanedDevice(null);
                        handleCleanup(dmOrphanedDevice.serial_number);
                    }}
                />
            )}
        </div>
    );
}

function DmActionSheet({ device, isSuppressed, onClose, onVlanCaps, onWanCaps, onToggleAlerts, onRemoveMonitoring, onDeleteData }) {
    return (
        <div className="dm-sheet-overlay" onClick={onClose}>
            <div className="dm-sheet" onClick={(e) => e.stopPropagation()}>
                <div className="dm-sheet-handle" />
                <div className="dm-sheet-title">{device.serial_number}</div>

                <button className="dm-sheet-action" style={{ color: 'var(--color-secondary)' }} onClick={onVlanCaps}>
                    VLAN CAPS
                </button>
                <button className="dm-sheet-action" style={{ color: 'var(--color-secondary)' }} onClick={onWanCaps}>
                    WAN CAPS
                </button>
                <button
                    className="dm-sheet-action"
                    style={{ color: isSuppressed ? 'var(--color-success)' : 'var(--color-accent)' }}
                    onClick={onToggleAlerts}
                >
                    {isSuppressed ? 'UNMUTE' : 'MUTE'}
                </button>
                <button className="dm-sheet-action" style={{ color: 'var(--color-accent)' }} onClick={onRemoveMonitoring}>
                    STOP MONITORING
                </button>
                <button className="dm-sheet-action" style={{ color: '#fff' }} onClick={onDeleteData}>
                    STOP &amp; REMOVE DATA
                </button>

                <button className="dm-sheet-cancel" onClick={onClose}>CANCEL</button>
            </div>
        </div>
    );
}

function DmOrphanedActionSheet({ device, onClose, onReadd, onDeleteData }) {
    const canReadd = device.organization_id && device.organization_id !== 'Unknown';

    return (
        <div className="dm-sheet-overlay" onClick={onClose}>
            <div className="dm-sheet" onClick={(e) => e.stopPropagation()}>
                <div className="dm-sheet-handle" />
                <div className="dm-sheet-title">{device.serial_number}</div>

                <button
                    className="dm-sheet-action"
                    style={{ color: canReadd ? 'var(--color-success)' : 'rgba(255,255,255,.35)', opacity: canReadd ? 1 : 0.5 }}
                    onClick={onReadd}
                    disabled={!canReadd}
                >
                    RE-ADD DEVICE
                </button>
                <button className="dm-sheet-action" style={{ color: '#fff' }} onClick={onDeleteData}>
                    DELETE DATA
                </button>

                <button className="dm-sheet-cancel" onClick={onClose}>CANCEL</button>
            </div>
        </div>
    );
}

export default DeviceManager;
